<template>
  <div class="flex flex-col w-[245px]">
    <div class="flex flex-col gap-2 p-2 border_bottom">
      <div class="flex items-center gap-2 px-3 pt-2">
        <button
          @click="goBack"
          class="p-1 hover:bg-[--white-opacity-4] rounded-md transition-colors"
        >
          <ThemeImage
            :icon="imageNames.BACK_ARROW_WHITE"
            alt="back"
            class="size-3.5"
          />
        </button>
        <span class="proxima_nova text-sm text_color"> Property visibility </span>
      </div>

      <div class="flex flex-col">
        <a-input
          class="input_medium"
          v-model:value="searchQuery"
          placeholder="Search columns"
          allow-clear
          autofocus
          @click.stop=""
          @keydown.stop="$event.stopPropagation()"
        >
          <template #prefix>
            <ThemeImage
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              class="size-4"
            />
          </template>
        </a-input>
      </div>
    </div>

    <div ref="scrollContainerRef" class="flex flex-col p-2 gap-1 max-h-[300px] overflow-y-auto">
      <SkeletonLoader v-if="isLoading" page="manage_columns" />

      <template v-else-if="filteredColumns.length > 0 && !searchQuery">
        <div
          v-for="column in frozenColumnsForDisplay"
          :key="column.slug"
          class="draggable_column_item flex items-center justify-between p-3 dropdown_item flex-shrink-0"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <a-tooltip title="Unfreeze columns to reorder" placement="left">
              <div class="flex-shrink-0 opacity-40 cursor-not-allowed">
                <ThemeImage
                  :icon="imageNames.PIN"
                  alt="frozen"
                  class="size-4"
                />
              </div>
            </a-tooltip>

            <div class="flex-shrink-0">
              <ThemeImage
                :icon="getPropertyTypeIcon(column.type)"
                :alt="column.type"
                class="size-4"
              />
            </div>

            <span class="text-sm truncate text_color">
              {{ column.name }}
            </span>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              v-if="!(isColumnPrimary(column.slug) && isColumnVisible(column.slug))"
              @click.stop="toggleColumnVisibility(column.slug)"
              :class="{ 'opacity-30 cursor-not-allowed': isLastVisibleColumn(column.slug) || isPrimaryBlockedColumn(column.slug) }"
            >
              <ThemeImage
                :icon="imageNames.EYE_GREY"
                alt="visible"
                class="size-4"
              />
            </button>
          </div>
        </div>

        <VueDraggableNext
          v-model="nonFrozenVisibleColumns"
          item-key="slug"
          handle=".drag_handle"
          ghost-class="ghost_item"
          class="flex flex-col gap-1"
          @start="handleSortStart"
          @end="handleSortEnd"
        >
          <div
            v-for="column in nonFrozenVisibleColumns"
            :key="column.slug"
            class="draggable_column_item flex items-center justify-between p-3 dropdown_item flex-shrink-0"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div
                class="drag_handle hover:cursor-grab active:cursor-grabbing flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              >
                <ThemeImage
                  :icon="imageNames.DRAGGABLE_DOTS"
                  alt="drag_handle"
                  class="size-4"
                />
              </div>

              <div class="flex-shrink-0">
                <ThemeImage
                  :icon="getPropertyTypeIcon(column.type)"
                  :alt="column.type"
                  class="size-4"
                />
              </div>

              <span class="text-sm truncate text_color">
                {{ column.name }}
              </span>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <button
                v-if="!(isColumnPrimary(column.slug) && isColumnVisible(column.slug))"
                @click.stop="toggleColumnVisibility(column.slug)"
                :class="{ 'opacity-30 cursor-not-allowed': isLastVisibleColumn(column.slug) || isPrimaryBlockedColumn(column.slug) }"
              >
                <ThemeImage
                  :icon="imageNames.EYE_GREY"
                  alt="visible"
                  class="size-4"
                />
              </button>
            </div>
          </div>
        </VueDraggableNext>

        <div
          v-if="hiddenColumnsForDisplay.length > 0"
          class="flex flex-col mt-3"
        >
          <div class="px-3 py-2">
            <span
              class="text-[11px] font-medium"
              style="color: var(--white-opacity-60)"
              >HIDDEN COLUMNS</span
            >
          </div>
          <div
            v-for="column in hiddenColumnsForDisplay"
            :key="column.slug"
            class="flex items-center justify-between p-3 dropdown_item flex-shrink-0"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div class="flex-shrink-0 opacity-40">
                <ThemeImage
                  :icon="getPropertyTypeIcon(column.type)"
                  :alt="column.type"
                  class="size-4"
                />
              </div>

              <span class="text-sm truncate text_color opacity-40">
                {{ column.name }}
              </span>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <button
                v-if="!isColumnPrimary(column.slug)"
                @click.stop="toggleColumnVisibility(column.slug)"
              >
                <ThemeImage
                  :icon="imageNames.EYE_OFF_GREY"
                  alt="hidden"
                  class="size-4"
                />
              </button>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="filteredColumns.length > 0 && searchQuery">
        <div
          v-for="column in filteredColumns"
          :key="column.slug"
          class="flex items-center justify-between p-3 dropdown_item flex-shrink-0"
          @click="toggleColumnVisibility(column.slug)"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <div
              :class="[
                'flex-shrink-0 transition-opacity',
                !isColumnVisible(column.slug) ? 'opacity-40' : '',
              ]"
            >
              <ThemeImage
                :icon="getPropertyTypeIcon(column.type)"
                :alt="column.type"
                class="size-4"
              />
            </div>

            <span
              :class="[
                'text-sm truncate text_color transition-opacity',
                !isColumnVisible(column.slug) ? 'opacity-40' : '',
              ]"
            >
              {{ column.name }}
            </span>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              v-if="!(isColumnPrimary(column.slug) && isColumnVisible(column.slug))"
              :class="{ 'opacity-30 cursor-not-allowed': isLastVisibleColumn(column.slug) || (isPrimaryBlockedColumn(column.slug) && isColumnVisible(column.slug)) }"
            >
              <ThemeImage
                :icon="
                  isColumnVisible(column.slug)
                    ? imageNames.EYE_GREY
                    : imageNames.EYE_OFF_GREY
                "
                :alt="isColumnVisible(column.slug) ? 'visible' : 'hidden'"
                class="size-4"
              />
            </button>
          </div>
        </div>
      </template>

      <div v-else class="w-full">
        <EmptyScreen
          source="filters_empty"
          text="No columns found!"
          padding="my-6"
          css="size-16"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useDragAutoScroll } from "@/src/utils/dragScrollUtils.js";
import { VueDraggableNext } from "vue-draggable-next";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  customObjectAttributeTypes,
  ATTRIBUTE_TYPES,
  EXCLUDED_COLUMN_IDS,
  DEAL_EXCLUDED_COLUMN_IDS,
  STANDARD_OBJECT_PRIMARY_ATTR_IDS,
  EXCLUDED_ATTRIBUTE_TYPES_IN_LIST_VIEWS,
} from "@/src/constants/crmConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import ProjectService from "@/src/api/ProjectService.js";
import ViewsServiceV2 from "@/src/api/ViewsServiceV2.js";
import DashboardService from "@/src/api/DashboardService.js";
import { useStore } from "vuex";
import {
  saveColumnOrder as saveColumnOrderUtil,
  buildSlugToIdMap,
} from "@/src/utils/dynamicCRMViewUtils.js";

const props = defineProps({
  slug: { type: String, required: true },
  currentColumns: { type: Array, default: () => [] },
  frozenColumns: { type: Array, default: () => [] },
  viewId: { type: [String, Number], default: null },
  viewType: { type: String, default: 'table' },
  projectId: { type: [String, Number], default: null },
  service: { type: String, default: null },
});

const emit = defineEmits([
  "columns-updated",
  "frozen-columns-updated",
  "go-back",
]);

const store = useStore();
const searchQuery = ref("");
const isLoading = ref(false);
const schemaColumns = ref([]);
const visibleColumns = ref([]);
const localFrozenColumns = ref([]);
const slugToIdMap = ref({});
const idToSlugMap = ref({});
const draggableColumns = ref([]);
const previousColumnOrder = ref([]);

const isTickets = computed(() => props.slug === entityTypes.TICKETS);
const isProjectTask = computed(() => props.slug === entityTypes.TASKS && props.projectId);
const isDeal = computed(() => props.slug === entityTypes.DEALS);

const isPrimaryBlockedColumn = (columnSlug) => {
  if (props.viewType !== "board") return false;
  const primarySlugs = STANDARD_OBJECT_PRIMARY_ATTR_IDS[props.slug];
  return Array.isArray(primarySlugs) && primarySlugs.includes(columnSlug);
};

const filteredColumns = computed(() => {
  let columns = schemaColumns.value.filter((col) => {
    if (col.slug === "id") return false;
    const type = String(col.type || col.attributeType || "").toLowerCase();
    if (EXCLUDED_ATTRIBUTE_TYPES_IN_LIST_VIEWS.includes(type)) return false;
    return true;
  });

  if (!searchQuery.value.trim()) return columns;

  const query = searchQuery.value.toLowerCase().trim();
  return columns.filter(
    (col) =>
      col.name.toLowerCase().includes(query) ||
      col.slug.toLowerCase().includes(query),
  );
});

const frozenColumnsForDisplay = computed(() => {
  return draggableColumns.value.filter(
    (col) =>
      visibleColumns.value.includes(col.slug) &&
      localFrozenColumns.value.includes(col.slug),
  );
});

const nonFrozenVisibleColumns = computed({
  get() {
    return draggableColumns.value.filter(
      (col) =>
        visibleColumns.value.includes(col.slug) &&
        !localFrozenColumns.value.includes(col.slug),
    );
  },
  set(newValue) {
    const frozenCols = frozenColumnsForDisplay.value;
    const hiddenCols = draggableColumns.value.filter(
      (col) => !visibleColumns.value.includes(col.slug),
    );
    draggableColumns.value = [...frozenCols, ...newValue, ...hiddenCols];
  },
});

const hiddenColumnsForDisplay = computed(() => {
  return draggableColumns.value.filter(
    (col) => !visibleColumns.value.includes(col.slug),
  );
});

const goBack = () => {
  emit("go-back");
};

const getPropertyTypeIcon = (type) => {
  const attributeType = customObjectAttributeTypes.find(
    (attr) => attr.value === type,
  );
  return attributeType ? attributeType.icon : imageNames.PROPERTY_TYPE_TEXT;
};

const isColumnVisible = (columnSlug) => {
  return visibleColumns.value.includes(columnSlug);
};

const isColumnPrimary = (columnSlug) => {
  const column = schemaColumns.value.find((col) => col.slug === columnSlug);
  return column?.is_primary === true;
};

const isColumnFrozen = (columnSlug) => {
  return localFrozenColumns.value.includes(columnSlug);
};

const isLastVisibleColumn = (columnSlug) => {
  return visibleColumns.value.length === 1 && visibleColumns.value.includes(columnSlug);
};

const toggleColumnVisibility = async (columnSlug) => {
  if (isColumnPrimary(columnSlug) && isColumnVisible(columnSlug)) {
    return;
  }

  if (isLastVisibleColumn(columnSlug)) {
    return;
  }

  if (isPrimaryBlockedColumn(columnSlug) && isColumnVisible(columnSlug)) {
    return;
  }

  const index = visibleColumns.value.indexOf(columnSlug);
  if (index > -1) {
    visibleColumns.value.splice(index, 1);

    if (isColumnFrozen(columnSlug)) {
      const frozenIndex = localFrozenColumns.value.indexOf(columnSlug);
      if (frozenIndex > -1) {
        localFrozenColumns.value.splice(frozenIndex, 1);
      }
    }
  } else {
    const allSlugsInOrder = draggableColumns.value.map((col) => col.slug);
    const newVisibleColumns = allSlugsInOrder.filter(
      (slug) => visibleColumns.value.includes(slug) || slug === columnSlug,
    );
    visibleColumns.value = newVisibleColumns;
  }

  await saveColumnChanges();
};

const saveColumnChanges = async () => {
  try {
    if (!props.viewId) {
      console.error("No view ID provided for saving column changes");
      emit("columns-updated", visibleColumns.value);
      emit("frozen-columns-updated", localFrozenColumns.value);
      return;
    }

    const orderedSlugs = draggableColumns.value.map((col) => col.slug);

    const orderedVisibleSlugs = orderedSlugs.filter((slug) =>
      visibleColumns.value.includes(slug),
    );

    const updatedFrozenColumns = orderedVisibleSlugs.filter((slug) =>
      localFrozenColumns.value.includes(slug),
    );

    const schemaObjectData = {
      properties: schemaColumns.value,
    };

    // For board views, update kanban_columns instead of columns
    const isBoardView = props.viewType === 'board';
    
    if (isBoardView) {
      // For board views, save directly to kanban_columns
      const apiVersion = isProjectTask.value ? "v2" : "v1";
      const payload = {
        kanban_columns: orderedVisibleSlugs
      };
      
      try {
        if (apiVersion === "v2") {
          await ViewsServiceV2.updateView(props.viewId, payload);
        } else {
          await DashboardService.applyFilter(props.viewId, payload);
        }
        
        localFrozenColumns.value = updatedFrozenColumns;
        previousColumnOrder.value = draggableColumns.value.map((col) => col.slug);
        emit("columns-updated", orderedVisibleSlugs);
        emit("frozen-columns-updated", updatedFrozenColumns);
      } catch (error) {
        console.error("Error saving kanban columns:", error);
        emit("columns-updated", visibleColumns.value);
        emit("frozen-columns-updated", localFrozenColumns.value);
      }
    } else {
      // For table views, use the existing column save logic
      const apiVersion = isProjectTask.value ? "v2" : "v1";

      const result = await saveColumnOrderUtil(
        props.viewId,
        orderedVisibleSlugs,
        schemaObjectData,
        apiVersion,
      );

      if (result.success) {
        localFrozenColumns.value = updatedFrozenColumns;
        previousColumnOrder.value = draggableColumns.value.map((col) => col.slug);
        emit("columns-updated", orderedVisibleSlugs);
        emit("frozen-columns-updated", updatedFrozenColumns);
      } else {
        emit("columns-updated", visibleColumns.value);
        emit("frozen-columns-updated", localFrozenColumns.value);
      }
    }
  } catch (error) {
    console.error("Error saving column changes:", error);
    emit("columns-updated", visibleColumns.value);
    emit("frozen-columns-updated", localFrozenColumns.value);
  }
};

const { scrollContainerRef, handleSortStart, handleSortEnd: stopDragScroll } = useDragAutoScroll();

const handleSortEnd = async () => {
  stopDragScroll();
  await handleDragEnd();
};

const handleDragEnd = async () => {
  const currentOrder = draggableColumns.value.map((col) => col.slug);
  const hasOrderChanged =
    currentOrder.length !== previousColumnOrder.value.length ||
    currentOrder.some(
      (slug, index) => slug !== previousColumnOrder.value[index],
    );

  if (hasOrderChanged) {
    await saveColumnChanges();
  }
};

const fetchSchemaColumns = async () => {
  if (!props.slug) return;

  try {
    isLoading.value = true;

    let response;
    if (isProjectTask.value) {
      response = await ProjectService.tasks.fetchTaskSchema(props.projectId);
    } else if (isTickets.value) {
      response = await TicketsService.getTicketSchema();
    } else {
      // Prefer the Vuex schema cache (service-aware) so opening the column
      // picker doesn't re-hit the API when the view already has the schema.
      const cached = store.getters["schema/getSchemaBySlug"](
        props.slug,
        null,
        props.service,
      );
      if (cached?.attributes?.length) {
        response = { success: true, schema: cached };
      } else {
        const dispatchRes = await store.dispatch("schema/fetchSchema", {
          slug: props.slug,
          service: props.service,
        });
        response = dispatchRes?.schema
          ? { success: true, schema: dispatchRes.schema }
          : { success: false };
      }
    }

    if (response?.success && response?.schema) {
      const allAttributes = response.schema.attributes || [];

      const excludedIds = new Set([
        ...EXCLUDED_COLUMN_IDS,
        ...(isDeal.value ? DEAL_EXCLUDED_COLUMN_IDS : []),
      ]);

      const excludedTypes = ["relationship", "image"];
      if (props.viewType === "board") {
        excludedTypes.push(ATTRIBUTE_TYPES.ACTION);
      }

      schemaColumns.value = allAttributes.filter(
        (attr) =>
          !excludedTypes.includes(String(attr.type || "").toLowerCase()) &&
          !excludedIds.has(attr.id) &&
          !excludedIds.has(attr.slug),
      );

      const { slugToIdMap: slugMap, idToSlugMap: idMap } = buildSlugToIdMap({
        properties: schemaColumns.value,
      });
      slugToIdMap.value = slugMap;
      idToSlugMap.value = idMap;

      if (props.currentColumns && props.currentColumns.length > 0) {
        visibleColumns.value = props.currentColumns
          .map((col) => {
            if (typeof col === "number" && idToSlugMap.value[col]) {
              return idToSlugMap.value[col];
            } else if (typeof col === "string" && col !== "id") {
              return col;
            }
            return null;
          })
          .filter((col) => col !== null);

        const columnsMap = new Map(
          schemaColumns.value.map((col) => [col.slug, col]),
        );
        draggableColumns.value = visibleColumns.value
          .map((slug) => columnsMap.get(slug))
          .filter(Boolean);

        const remainingColumns = schemaColumns.value.filter(
          (col) =>
            col.slug !== "id" && !visibleColumns.value.includes(col.slug),
        );
        draggableColumns.value.push(...remainingColumns);
      } else if (props.viewType === 'board') {
        let primarySlugs = schemaColumns.value
          .filter((col) => col.is_primary === true && col.slug !== "id")
          .map((col) => col.slug);
        if (primarySlugs.length === 0) {
          const standardPrimary = STANDARD_OBJECT_PRIMARY_ATTR_IDS[props.slug] || [];
          primarySlugs = schemaColumns.value
            .filter((col) => standardPrimary.includes(col.slug))
            .map((col) => col.slug);
        }
        visibleColumns.value = primarySlugs;
        draggableColumns.value = schemaColumns.value.filter(
          (col) => col.slug !== "id",
        );
      } else {
        visibleColumns.value = schemaColumns.value
          .filter((col) => col.slug !== "id")
          .map((col) => col.slug);
        draggableColumns.value = schemaColumns.value.filter(
          (col) => col.slug !== "id",
        );
      }

      const primaryColumns = schemaColumns.value
        .filter((col) => col.is_primary === true && col.slug !== "id")
        .map((col) => col.slug);

      primaryColumns.forEach((slug) => {
        if (!visibleColumns.value.includes(slug)) {
          visibleColumns.value.push(slug);
        }
      });

      previousColumnOrder.value = draggableColumns.value.map((col) => col.slug);
    }
  } catch (error) {
    console.error("Error fetching schema columns:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchSchemaColumns();
  localFrozenColumns.value = [...props.frozenColumns];
});

watch(
  () => props.slug,
  () => {
    fetchSchemaColumns();
  },
);

watch(
  () => props.frozenColumns,
  (newFrozenColumns) => {
    localFrozenColumns.value = [...newFrozenColumns];
  },
  { deep: true },
);

watch(
  () => props.currentColumns,
  (newColumns) => {
    if (
      newColumns &&
      newColumns.length > 0 &&
      Object.keys(idToSlugMap.value).length > 0
    ) {
      visibleColumns.value = newColumns
        .map((col) => {
          if (typeof col === "number" && idToSlugMap.value[col]) {
            return idToSlugMap.value[col];
          } else if (typeof col === "string" && col !== "id") {
            return col;
          }
          return null;
        })
        .filter((col) => col !== null);

      const columnsMap = new Map(
        schemaColumns.value.map((col) => [col.slug, col]),
      );
      draggableColumns.value = visibleColumns.value
        .map((slug) => columnsMap.get(slug))
        .filter(Boolean);

      const remainingColumns = schemaColumns.value.filter(
        (col) => col.slug !== "id" && !visibleColumns.value.includes(col.slug),
      );
      draggableColumns.value.push(...remainingColumns);

      const primaryColumns = schemaColumns.value
        .filter((col) => col.is_primary === true && col.slug !== "id")
        .map((col) => col.slug);

      primaryColumns.forEach((slug) => {
        if (!visibleColumns.value.includes(slug)) {
          visibleColumns.value.push(slug);
        }
      });

      previousColumnOrder.value = draggableColumns.value.map((col) => col.slug);
    }
  },
  { deep: true },
);
</script>

<style scoped>
.ghost_item {
  opacity: 0.5;
  background: var(--white-opacity-8);
}

.draggable_column_item {
  transition: all 0.2s ease;
}

.drag_handle {
  user-select: none;
}
</style>